/**
 * Global Window Manager for handling z-index layering of windowed explorers
 * 
 * Ensures proper window stacking order when multiple explorers are open:
 * - Clicked/dragged windows come to front
 * - Other windows are pushed to background layers
 * - Maintains proper hierarchy: ToolBox (2000) > Explorers (1000-1999) > Map Controls (500)
 */

interface WindowInfo {
    id: string;
    element: HTMLElement;
    currentZIndex: number;
}

class WindowManager {
    private windows: Map<string, WindowInfo> = new Map();
    private nextZIndex: number = 1000; // Starting z-index for explorers
    private maxZIndex: number = 1999;   // Maximum z-index for explorers (below ToolBox at 2000)

    /**
     * Register a new windowed explorer
     */
    registerWindow(id: string, element: HTMLElement): number {
        const zIndex = this.nextZIndex++;

        // Ensure we don't exceed our maximum
        if (this.nextZIndex > this.maxZIndex) {
            this.nextZIndex = 1000; // Reset and compact
            this.compactZIndexes();
        }

        this.windows.set(id, {
            id,
            element,
            currentZIndex: zIndex
        });

        return zIndex;
    }

    /**
     * Unregister a window when it's closed
     */
    unregisterWindow(id: string): void {
        this.windows.delete(id);
    }

    /**
     * Bring a window to the front and push others back
     */
    bringToFront(id: string): number {
        const window = this.windows.get(id);
        if (!window) {
            console.warn(`🪟 WindowManager: Window ${id} not found`);
            return 1000;
        }

        const previousZIndex = window.currentZIndex;

        // Find the current highest z-index among all windows
        let highestZIndex = 1000;
        for (const [_, win] of this.windows) {
            if (win.currentZIndex > highestZIndex) {
                highestZIndex = win.currentZIndex;
            }
        }

        // If this window is already at the front, no need to change
        if (window.currentZIndex === highestZIndex && this.windows.size === 1) {
            return window.currentZIndex;
        }

        const newZIndex = Math.min(highestZIndex + 1, this.maxZIndex);
        window.currentZIndex = newZIndex;
        return newZIndex;
    }

    /**
     * Compact z-indexes to prevent overflow
     */
    private compactZIndexes(): void {
        const sortedWindows = Array.from(this.windows.values())
            .sort((a, b) => a.currentZIndex - b.currentZIndex);

        let zIndex = 1000;
        for (const window of sortedWindows) {
            window.currentZIndex = zIndex;
            zIndex++;
        }

        this.nextZIndex = zIndex;
    }

    /**
     * Update window element reference (useful when component re-renders)
     */
    updateWindowElement(id: string, element: HTMLElement): void {
        const window = this.windows.get(id);
        if (window) {
            window.element = element;
        }
    }

    /**
     * Get current z-index for a window
     */
    getZIndex(id: string): number {
        const window = this.windows.get(id);
        return window?.currentZIndex || 1000;
    }

    /**
     * Get all registered window IDs
     */
    getWindowIds(): string[] {
        return Array.from(this.windows.keys());
    }

    /**
     * Get window count
     */
    getWindowCount(): number {
        return this.windows.size;
    }
}

// Export singleton instance
export const windowManager = new WindowManager();

/**
 * Generate a unique window ID for explorer instances
 */
export const generateWindowId = (prefix: string = 'explorer'): string => {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};